import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import moment from "moment";
import { EQueueConfigType, WidgetType } from "src/Types/ReDoc/EQueue";
import { EQModalStates, ReturnViewMode } from "src/Views/EQueue";
import { ReturnPultStatusType, ReturnQueueItemType, ReturnStateType } from "src/Types/ReDoc/Return";
import { Select2WebElementType } from "src/Types/ReDoc";
import { returnGetQueue, returnGetQueueState, returnGetWindows } from "./ReturnThunks";

/** Redux-состояние виджета re:turn */
interface IReturnSlicerState {
	/** Общий статус асинхронных запросов */
	loading: "idle" | "pending" | "succeeded" | "failed";
	/** Последняя ошибка запросов */
	error?: string;
	/** Сохраненная конфигурация окна */
	config: EQueueConfigType;
	/** Техническое состояние виджета (совместимость с общей ЭО логикой) */
	widgetState: WidgetType;
	/** Текущая открытая модалка */
	modalState: EQModalStates;
	/** Счетчики очереди */
	queueCount: number;
	queuePersonCount: number;
	allCount: number;
	/** Талоны для вызова "по номеру" */
	queue: ReturnQueueItemType[];
	/** Доступные окна для перенаправления */
	windows: Select2WebElementType[];
	/** Статус загрузки списка окон */
	windowsStatus: "idle" | "loading" | "loaded";
	/** Кастомное состояние re:turn для ReturnWidget */
	viewMode: ReturnViewMode;
	/** Время начала перерыва (ISO string) — используется для таймера */
	pauseStartTime?: string;
	/** Текущий обслуживаемый/вызванный талон */
	ticket?: {
		id: string;
		number: string;
		inviteTime?: string;
		startTime?: string;
		description: string;
	};
	status: ReturnStateType;
}

export const PAUSE_START_KEY = "return_pause_start";

/** Начальное состояние re:turn */
const initialState: IReturnSlicerState = {
	loading: "idle",
	modalState: EQModalStates.None,
	widgetState: WidgetType.None,
	config: {
		contactId: "",
		login: "",
		password: "",
		officeId: -1,
		pultId: "",
		pultName: "",
		queueServerAddress: ""
	},
	queueCount: 0,
	queuePersonCount: 0,
	allCount: 0,
	queue: [],
	windows: [],
	windowsStatus: "idle",
	viewMode: "inactive",
	status: {
		pultStatus: ReturnPultStatusType.Offline,
		message: ""
	}
};

const returnSlicer = createSlice({
	name: "eQueueReturn",
	initialState,
	reducers: {
		/** Сохранить конфигурацию окна */
		setConfig: (state, action: PayloadAction<EQueueConfigType>) => {
			state.config = action.payload;
		},
		/** Принудительно переключить viewMode (кнопочные сценарии re:turn) */
		setViewMode: (state, action: PayloadAction<ReturnViewMode>) => {
			state.viewMode = action.payload;
			switch (action.payload) {
				case "inactive":
					state.widgetState = WidgetType.None;
					break;
				case "free":
					state.widgetState = WidgetType.Waiting;
					break;
				case "confirming":
				case "called":
					state.widgetState = WidgetType.Calling;
					break;
				case "working":
					state.widgetState = WidgetType.Working;
					break;
			}
		},
		/** Очистить текущий талон из виджета */
		clearMockTicket: (state) => {
			state.ticket = undefined;
		},
		/** Обновить состояние из backend ответа pultStatus/start/call/transfer/... */
		updateState: (state, action: PayloadAction<ReturnStateType>) => {
			state.status = action.payload;
			const ticket = action.payload.ticket;
			if (ticket) {
				state.ticket = {
					id: ticket.id,
					number: ticket.number,
					description: ticket.operation
				};
				if (ticket.inviteTime) {
					state.ticket.inviteTime = moment(ticket.inviteTime, "HH:mm:ss").toJSON();
				}
				if (ticket.startTime) {
					state.ticket.startTime = moment(ticket.startTime, "HH:mm:ss").toJSON();
				}
			}
			else {
				state.ticket = undefined;
			}
			const newWidgetState = getWidgetStatus(action.payload.pultStatus);
			state.widgetState = newWidgetState;
			switch (newWidgetState) {
				case WidgetType.None:
					state.viewMode = "inactive";
					state.pauseStartTime = undefined;
					break;
				case WidgetType.Waiting:
					state.viewMode = "free";
					state.pauseStartTime = undefined;
					break;
				case WidgetType.Calling:
					state.viewMode = "called";
					state.pauseStartTime = undefined;
					break;
				case WidgetType.Working:
					state.viewMode = "working";
					state.pauseStartTime = undefined;
					break;
				case WidgetType.OnPause:
					if ( state.viewMode !== "pause" ) {
						state.pauseStartTime = localStorage.getItem(PAUSE_START_KEY) ?? new Date().toISOString();
					}
					state.viewMode = "pause";
					break;
				default:
					state.viewMode = "inactive";
					state.pauseStartTime = undefined;
			}
		},
		/** Закрыть активную модалку */
		closeModal: (state) => {
			state.modalState = EQModalStates.None;
		},
		/** Открыть форму входа */
		openLoginForm: (state) => {
			state.modalState = EQModalStates.Login;
		},
		/** Открыть модалку вызова по талону */
		inviteByNumberModal: (state) => {
			state.modalState = EQModalStates.AscCallByNumber;
		},
		/** Открыть модалку подтверждения выхода */
		askExitModal: (state) => {
			state.modalState = EQModalStates.Exit;
		},
		/** Полный выход из re:turn (сброс UI) */
		exit: (state, action: PayloadAction<ReturnStateType>) => {
			state.status = action.payload;
			state.modalState = EQModalStates.None;
			state.widgetState = WidgetType.None;
			state.viewMode = "inactive";
			state.ticket = undefined;
			state.pauseStartTime = undefined;
		}
	},
	extraReducers: (builder) => {
		// Запрос счетчиков очереди
		builder.addCase(returnGetQueueState.pending, (state) => {
			state.loading = "pending";
		});
		builder.addCase(returnGetQueueState.fulfilled, (state, action) => {
			if (action.payload && typeof action.payload !== "string") {
				if (!action.payload.hasError) {
					const queueCount = action.payload.result?.queueCount ?? 0;
					const defferedCount = action.payload.result?.defferedCount ?? 0;
					const transferedCount = action.payload.result?.transferedCount ?? 0;
					state.queueCount = queueCount;
					state.queuePersonCount = defferedCount;
					state.allCount = queueCount + defferedCount + transferedCount;
					state.loading = "succeeded";
				}
				else {
					state.error = action.payload.errorMessage;
					state.loading = "failed";
				}
			}
		});
		builder.addCase(returnGetQueueState.rejected, (state, action) => {
			state.error = action.error.message;
			state.loading = "failed";
		});
		// Список талонов для модалки "вызов по номеру"
		builder.addCase(returnGetQueue.fulfilled, (state, action) => {
			if (action.payload && typeof action.payload !== "string" && !action.payload.hasError) {
				state.queue = Array.isArray(action.payload.result) ? action.payload.result.map((item) => ({
					...item,
					id: String(item.id)
				})) : [];
			}
		});
		// Список окон для перенаправления
		builder.addCase(returnGetWindows.pending, (state) => {
			state.loading = "pending";
			state.windowsStatus = "loading";
		});
		builder.addCase(returnGetWindows.fulfilled, (state, action) => {
			state.loading = "succeeded";
			state.windowsStatus = "loaded";
			state.windows = action.payload;
		});
		builder.addCase(returnGetWindows.rejected, (state, action) => {
			state.error = action.error.message;
			state.loading = "failed";
			state.windowsStatus = "loaded";
		});
	}
});

/** Сопоставление backend-статуса пульта в общий WidgetType */
const getWidgetStatus = (status: number): WidgetType => {
	switch (status) {
		case ReturnPultStatusType.Calling:
			return WidgetType.Calling;
		case ReturnPultStatusType.OnPause:
			return WidgetType.OnPause;
		case ReturnPultStatusType.Waiting:
			return WidgetType.Waiting;
		case ReturnPultStatusType.Working:
			return WidgetType.Working;
		default:
			return WidgetType.None;
	}
};

/** Преобразование времени от backend к формату для UI */
const getDateTime = (value: string): moment.Moment => {
	return moment(value, "HH:mm:ss").subtract({ hours: 2, seconds: 8 });
};

export default returnSlicer;
